import { Box, Typography } from "@mui/material";
import { CatSelectIcon, DogSelectIcon, OtherSelectIcon } from "../../../components/Icons";
import { NotificationCards, NotificationInnerStyle, NotificationUnreadDot } from "./Notification.style";
import React, { useState } from "react";

import { AvatarCustom } from "../../../components";
import { CTA_ACTIONS, NOTIFICATIONSUBCATEGORY } from "./NotificationContainer";
import { CustomToolTip } from "../EditHospitalContainer/EditHospitalContainer.style";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { Notification } from "../../../services/notifications/notifications-interface";
import { timeAgo } from "../../../utils/date";
import { userActivityLogger } from "../../../utils/mixpanel";
import { useCombinedAlertsDataStore } from "../../../hooks";
import { useNavigate } from "react-router-dom";
import { PAGES_PATH } from "../../../navigation/params";

type Props = {
    notification: Notification
    getIcon: (notification: Notification) => any;
    handleReadMessage?: (notificationId: string, notificationType:string) => void;
    handleCTA: (notification: Notification) => void;
}

const NotificationCard = (props: Props) => {
  const { notification, getIcon, handleReadMessage, handleCTA } = props;
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const { scratchpayRenewalPending } = useCombinedAlertsDataStore();
  const navigate = useNavigate();

  const isScratchPayDelinquencyNotification = (notification: Notification) => {
    const hasNoExplicitCta = !Object.values(CTA_ACTIONS).includes(notification.ctaName);
    const mentionsScratchPay = /scratchpay/i.test(notification.description || "");
    const isDelinquencyCopy =
      /payment overdue/i.test(notification.title || "") ||
      /plan has been paused/i.test(notification.title || "");

    return hasNoExplicitCta && mentionsScratchPay && isDelinquencyCopy;
  };

  const handleExpandDescription = (e: any) => {
    e.stopPropagation();
    setShowFullDescription(!showFullDescription);
  };

  const handleNotificationCtaClick=(e: React.MouseEvent<HTMLButtonElement>)=>{
    e.stopPropagation();
    if (handleReadMessage) {
      handleReadMessage(notification.notificationMessageId, notification.title);
    }
    handleCTA(notification);
  };

  const handleScratchPayDelinquencyNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleNotificationClick(notification.subCategory, notification.status, notification.ctaName);
    userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_NOTIFICATION_CLICKED, {
      provider: "scratchpay",
      delinquency_state: /plan has been paused/i.test(notification.title || "") ? "paused" : "overdue_notice",
      entry_point: "notifications",
      surface: "notification",
      pet_id: notification.ctaData?.petId,
      pet_name: notification.ctaData?.petName,
      is_scratchpay: true,
      payment_processor: "scratchpay",
      has_payment_url: false,
      payment_url_source: "none",
      referred_from_page: "notifications",
    });
    if (handleReadMessage) {
      handleReadMessage(notification.notificationMessageId, notification.title);
    }
    navigate(`${PAGES_PATH.USER_PROFILE}/${PAGES_PATH.USER_BILLING}`);
  };


  const handleNotificationClick = (notificationType: string, notificationStatus:string, ctaName: string) => {
    let typeWithDeliquency;

    switch (notificationType) {
    case "failedInvoice":
      typeWithDeliquency = ctaName === "contactHospital" ? "wp_cancelled" : "wp_paused";
      break;
    case "referralNotification":
      typeWithDeliquency = "refer_a_friend";
      break;
    default:
      typeWithDeliquency = notificationType;
    }

    userActivityLogger(MIXPANEL_EVENTS.NOTIFICATION_CLICK, {
      notification_type: typeWithDeliquency,
      notification_status: notificationStatus,
      cta_title: ctaName,
    });
  };
  const handleExpandCard = (e: any) => {
    if (Object.values(CTA_ACTIONS).includes(notification.ctaName)) {
      setIsCardExpanded(!isCardExpanded);
      if (notification.description.length>85) {
        handleExpandDescription(e);
      }
    }
    if (notification.description.length>85) {
      handleExpandDescription(e);
    }
  };

  const getSpeciesIcon = (species: string)=>{
    if (species === "Dog") {
      return <DogSelectIcon width={40} height={40}/>;
    } else if (species === "Cat") {
      return <CatSelectIcon width={40} height={40}/>;
    } else if (species === "Other") {
      return <OtherSelectIcon width={40} height={40}/>;
    } else {
      return <AvatarCustom image={getIcon && getIcon(notification)} width={40} height={40} />;
    }
  };

  const getCTADataForExpiredCard = () => {
    const ctaName = /disabled/i.test(notification.subCategory)? "Add Payment Method" : "Update Now";

    return { isCta: true, ctaName: ctaName };
  };
  const getCTASTAT = (notification: Notification) => {
    switch (notification.ctaName) {
    case CTA_ACTIONS.BOOKAVISIT:
      return { isCta: true, ctaName: "Come meet the team!" };
    case CTA_ACTIONS.BOOKAVISITWITHPETSELECTED:
      return { isCta: true, ctaName: `Book a visit for ${notification.ctaData.petName}` };
    case CTA_ACTIONS.PHONELOGINREMINDER:
      return { isCta: true, ctaName: "Verify Now" };
    case CTA_ACTIONS.CUSTOMERREVIEW:
      return { isCta: true, ctaName: "Post a review" };
    case CTA_ACTIONS.PAYINVOICE:
    case CTA_ACTIONS.PAYNOW:
      return { isCta: true, ctaName: "Pay Now" };
    case CTA_ACTIONS.CONTACTHOSPITAL:
      return { isCta: true, ctaName: "Contact Hospital" };
    case CTA_ACTIONS.EXPRESSVISIT:
      return { isCta: true, ctaName: "Book an Express Service" };
    case CTA_ACTIONS.UPDATEEXPIREDCARD:
      return getCTADataForExpiredCard();
    case CTA_ACTIONS.RENEWPLAN:
      return { isCta: true, ctaName: "Renew Plan" };
    case CTA_ACTIONS.MANAGEPLAN:
      return { isCta: true, ctaName: "Manage Plan" };
    case CTA_ACTIONS.UPDATEPAYMENTMETHOD:
      return { isCta: true, ctaName: "Update Payment Method" };
    case CTA_ACTIONS.SUBSCRIBE:
      return { isCta: true, ctaName: "Subscribe" };
    case CTA_ACTIONS.SEEPLAN:
      return { isCta: true, ctaName: "See Plan" };
    case CTA_ACTIONS.ADDPAYMENTMETHOD:
      return { isCta: true, ctaName: "Add Payment Method" };
    case CTA_ACTIONS.BOOKNOW:
      return { isCta: true, ctaName: "Book Now" };
    case CTA_ACTIONS.REFERFRIEND:
    case CTA_ACTIONS.REFERNOW:
      return { isCta: true, ctaName: "Refer Now" };
    case CTA_ACTIONS.REVIEWNEWPLANS:
      if (notification.subCategory === NOTIFICATIONSUBCATEGORY.SCRATCHPAYRENEWALREMINDER1DAYAFTER) {
        const petId = notification.ctaData.petId;
        const renewalData = scratchpayRenewalPending?.find((p) => p.petId === petId);
        if (renewalData) {
          return { isCta: true, ctaName: "Apply Now with ScratchPay" };
        }
        return { isCta: false, ctaName: "" };
      }
      return { isCta: true, ctaName: "Review New Plans" };
    default:
      return { isCta: false, ctaName: "" };
    }
  };


  return (
    <>
      <NotificationCards onClick={(e: any) => {
        handleExpandCard(e),
        handleNotificationClick(notification.subCategory, notification.status, notification.ctaName);
      }}>
        <Box>
          { notification?.imageUrl ? <AvatarCustom image={getIcon && getIcon(notification)} width={40} height={40} /> :
            getSpeciesIcon(notification?.ctaData?.petSpecies)}</Box>
        <Box sx={NotificationInnerStyle}>
          <Box
            sx={{ cursor: isScratchPayDelinquencyNotification(notification) ? "pointer" : "default" }}
            onClick={
              isScratchPayDelinquencyNotification(notification) ?
                handleScratchPayDelinquencyNavigate :
                undefined
            }
          >
            <Typography
              variant="body1"
              sx={{
                position: "relative",
                fontWeight: "600",
                maxWidth: "90%",
                wordWrap: "break-word",
              }}
            >
              {notification.title}
            </Typography>
            <Box sx={{ position: "relative", display: "flex", justifyContent: "space-between",
              alignItems: "flex-start" }}>

              <Typography variant="body1" sx={{ color: (theme)=>theme.palette.gray.main, flex: 1 }}>
                {notification.description ?
                  showFullDescription ?
                    notification.description :
                    notification.description.length > 99 ?
                      notification.description.substring(0, 85) + "..." :
                      notification.description :
                  ""}</Typography>

              {(notification.description.length > 99 || getCTASTAT(notification)?.isCta) &&
                <Box sx={{ width: "17px", height: "10px", cursor: "pointer", flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExpandCard(e);
                  }}>
                  <ExpandMoreIcon width={10} height={10} color="primary"
                    sx={{ transition: "all 0.3s ease-in-out",
                      transform: `${showFullDescription || isCardExpanded ? "rotate(180deg)": undefined }` }}
                  />
                </Box>}
            </Box>
          </Box>

          <Typography variant="body1" sx={{ fontSize: "12px",
            color: "#908E8D" }}>{timeAgo(notification.createdAt)}</Typography>

          {isCardExpanded && <Typography variant="body1" fontWeight="bold" color="#E5AF0A" className="!cursor-pointer"
            onClick={handleNotificationCtaClick}>{getCTASTAT(notification)?.ctaName}</Typography>}


          {notification.status === "unread" &&
            <CustomToolTip
              title={
                <Typography color="inherit">Mark as read</Typography>}
              placement="left"
              sx={{ marginLeft: "1rem !important" }}>
              <NotificationUnreadDot onClick={
                ()=>handleReadMessage && handleReadMessage(notification.notificationMessageId, notification.title)
              }/>
            </CustomToolTip>
          }
        </Box>
      </NotificationCards>
    </>
  );
};

export default NotificationCard;
